import { WarnIcon } from "@assets";
import {
  Button,
  ButtonProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useMediaQuery,
} from "@chakra-ui/react";
import Dialog, { DialogProps } from "@components/Dialog/Dialog";
import { to, unique } from "@lib/utils";
import { normalizeEmail } from "@models/utils";
import { SignInModal, useUserStore } from "@providers/auth";
import * as Sentry from "@sentry/browser";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import {
  PaymentStep,
  checkoutPayment,
  saveTransaction,
  usePaymentStore,
} from "../../payment";

const CheckoutButton = ({
  allowSubmit,
  customButtonProps,
}: {
  allowSubmit: boolean;
  customButtonProps?: ButtonProps;
}) => {
  const transaction = usePaymentStore((state) => state.transaction);
  const currentStep = usePaymentStore((state) => state.currentStep);
  const [isLoading, setIsLoading] = useState(false);
  const [staticDialog, setStaticDialog] = useState<DialogProps | null>(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isMobile] = useMediaQuery("(max-width: 640px)");
  const t = (key?: string) => key || "";

  const loadingDialog: DialogProps = {
    loading: true,
    isClose: false,
    children: t(
      "We're currently processing your order. This could take up to a few minutes."
    ),
  };

  const errorDialog: DialogProps = {
    onClose: () => setStaticDialog(null),
    icon: WarnIcon,
    title: t("Error"),
    actions: [
      {
        text: t("Dismiss"),
        primary: true,
        onClick: () => {
          setIsLoading(false);
          setStaticDialog(null);
        },
      },
    ],
    children: t(
      "Something went wrong. Please try again later or contact our support."
    ),
  };

  const purchaseLimitDialog: DialogProps = {
    icon: WarnIcon,
    title: t("Error"),
    actions: [
      {
        text: t("Dismiss"),
        onClick: () => {
          setIsLoading(false);
          /* backToEvent(); */
          setStaticDialog(null);
        },
      },
    ],
    isClose: false,
    children: t("You have already reached the maximum number of purchases."),
  };

  const duplicateEmailDialog: DialogProps = {
    icon: WarnIcon,
    title: t("Error"),
    actions: [
      {
        text: t("Dismiss"),
        onClick: () => {
          setIsLoading(false);
          setStaticDialog(null);
        },
      },
    ],
    children: t("You can only buy 1 ticket for each email address."),
  };

  const selectedItems = useMemo(
    () => !!(transaction?.items && transaction.items.length > 0),
    [transaction?.items.length]
  );

  const handleCheckout = async () => {
    Sentry.addBreadcrumb({
      category: "checkout:proceedToCheckout",
      level: "info",
    });
    window.scrollTo(0, 0);
    setIsLoading(true);
    const _transaction = usePaymentStore.getState().transaction;
    console.log("handleCheckout", _transaction);
    if (!_transaction?.id || isEmpty(_transaction.items))
      return setStaticDialog(errorDialog);

    const { err } = await to(saveTransaction(_transaction));
    if (err) return setStaticDialog(errorDialog);

    setStaticDialog(null);
    setIsLoading(false);
    if (!useUserStore.getState().user) return setShowSignInModal(true);
    usePaymentStore.setState({ currentStep: PaymentStep.CHECKOUT });
  };

  const handleSubmit = async () => {
    Sentry.addBreadcrumb({
      category: "checkout:submitPayment",
      level: "info",
    });
    window.scrollTo(0, 0);
    const _transaction = usePaymentStore.getState().transaction;
    const _user = useUserStore.getState().user;
    const _event = usePaymentStore.getState().event;
    if (
      !allowSubmit ||
      !_transaction ||
      !_user ||
      !_event ||
      isEmpty(_transaction.items)
    )
      return;
    const tier = _event.tiers.find(
      (tier) => tier.id === _transaction.items[0].productId
    );
    if (!tier) return;

    setIsLoading(true);
    setStaticDialog(loadingDialog);
    console.log("checkout:submitPayment", _transaction);

    Sentry.setTag("event.id", _transaction.eventId);
    Sentry.setTag("transaction.id", _transaction.id);
    Sentry.setTag("transaction.productId", tier.id);
    Sentry.setTag("transaction.email", _transaction.contact.email);
    Sentry.setTag("transaction.user", _transaction.contact.name);
    Sentry.setTag("transaction.wallet", _transaction.contact.wallet);
    Sentry.setTag("transaction.quantity", _transaction.items.length);
    Sentry.setTag(
      "transaction.products",
      _transaction.items.map((item) => item.name || item.productId).join(",")
    );
    Sentry.setTag("transaction.amount", _transaction.finalAmount);
    Sentry.setTag("transaction.currency", _transaction.currency);
    Sentry.setTag("transaction.discountAmount", _transaction.discountAmount);
    Sentry.setTag(
      "transaction.discountCode",
      _transaction.discount?.code || ""
    );
    Sentry.setTag("transaction.fee", _transaction.transactionFee);
    Sentry.setTag("transaction.VAT", _transaction.vatRate || 0);
    Sentry.setTag("transaction.paymentMethod", _transaction.paymentMethod);
    Sentry.setUser({
      id: _user.id,
      email: _user.email,
    });

    // Check unique emails
    const uniqueEmails = unique(
      _transaction.items
        .map((item) => item.contact.email || "")
        .map((email) => normalizeEmail(email))
    );
    if (uniqueEmails.length !== _transaction.items.length) {
      setStaticDialog(duplicateEmailDialog);
      return;
    }

    // // Check pervious purchase
    // if (tier.paymentConfig?.maxPerUser && tier.paymentConfig.maxPerUser > 0) {
    //   const previousCount = await validatePreviousTransactions(
    //     _event.id,
    //     tier.id
    //   );
    //   if (
    //     previousCount + _transaction.items.length >
    //     tier.paymentConfig.maxPerUser
    //   ) {
    //     setStaticDialog(purchaseLimitDialog);
    //     return;
    //   }
    // }

    const successUrl = `${window.location.origin}${window.location.pathname}/success`;
    const { err, res: url } = await to(checkoutPayment(_transaction), 10);
    if (url === true) {
      return (window.location.href = successUrl);
    } else if (url) {
      return (window.location.href = url);
    } else {
      if (err) {
        if (String(err).includes("timeout")) {
          return (window.location.href = successUrl);
        }
        console.error("checkout:submitPayment", err);
      }
      return (window.location.href = `${window.location.origin}${window.location.pathname}/cancel`);
    }
  };

  return (
    <>
      <div className="flex flex-col items-end ml-auto sm:w-full sm:p-4">
        {currentStep == PaymentStep.SELECT_ITEMS && (
          <Button
            // variant="gradient"
            isDisabled={!selectedItems}
            fontSize="lg"
            px="1.5rem"
            py="0.70rem"
            width="100%"
            fontWeight="bold"
            onClick={handleCheckout}
            /* rightIcon={ */
            /*   <ArrowForwardIcon */
            /*     w="1.5rem" */
            /*     h="1.5rem" */
            /*     color={ */
            /*       !selectedItems */
            /*         ? useColorModeValue("white", "neutral.600") */
            /*         : "white" */
            /*     } */
            /*   /> */
            /* } */
            {...customButtonProps}
          >
            {t("Next")}
          </Button>
        )}
        {currentStep == PaymentStep.CHECKOUT && (
          <Button
            fontSize="lg"
            px="1.5rem"
            py="0.70rem"
            width="100%"
            fontWeight="bold"
            onClick={handleSubmit}
            /* rightIcon={ */
            /*   <ArrowForwardIcon */
            /*     w="1.5rem" */
            /*     h="1.5rem" */
            /*     color={ */
            /*       !selectedItems || isLoading || !allowSubmit */
            /*         ? useColorModeValue("white", "neutral.600") */
            /*         : "white" */
            /*     } */
            /*   /> */
            /* } */
            isLoading={isLoading}
            isDisabled={!selectedItems || isLoading || !allowSubmit}
            {...customButtonProps}
          >
            {t("Checkout")}
          </Button>
        )}
      </div>

      {staticDialog && (
        <Dialog
          isOpen={true}
          isClose={staticDialog.isClose}
          onClose={staticDialog.onClose}
          title={staticDialog.title}
          actions={staticDialog.actions}
          loading={staticDialog.loading}
          icon={staticDialog.icon}
          onOpen={staticDialog.onOpen}
        >
          <p className="w-full text-center">{staticDialog.children}</p>
        </Dialog>
      )}

      <Modal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        isCentered
        size={isMobile ? "full" : "md"}
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <SignInModal onAuthenticated={() => setShowSignInModal(false)} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CheckoutButton;
